import MarketUsageTree from "./MarketUsageTree";
import { platformLabel } from "@/pages/MarketIntelligence/PlatformIcon";
import type { MarketPlatform } from "@/pages/MarketIntelligence/model";
import { getAdminProductFeatures } from "@/utils/adminProductFeatures";
import { useAdminContext } from "@/contexts/AdminContext";
import { useAuth } from "@/contexts/AuthContext";
import { Permission } from "@/contexts/AuthContext/permissions";
import { useToast } from "@/contexts/ToastContext";
import {
  useTranslation,
  type LocaleCode,
  type Translate,
} from "@client-app/i18n";
import {
  CreditGrant as ApiCreditGrant,
  billingService,
  CreditSummaryResponse,
  CreditTopupEligibilityResponse,
  CreditTopupSku,
  CreditUsageEvent,
} from "@common/api/billingService";
import {
  CaretLeft,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Plus,
} from "@phosphor-icons/react";
import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import BuyMoreCreditsModal from "./BuyMoreCreditsModal";
import {
  buildCreditTopupReturnClearedUrl,
  clearPendingCreditTopupCheckoutSessionId,
  readCreditTopupReturnState,
  readPendingCreditTopupCheckoutSessionId,
} from "./creditTopupReturnParams";
import Modal from "./Modal";
import {
  resolveShopifyPendingPaymentNotice,
  resolveUsagePlanCreditRefreshState,
} from "./usagePlanCreditRefreshState";
import { resolveUsageStatus, type UsageStatus } from "./usageStatus";
import { resolveUsagePlanDisplayLabel } from "./usagePlanDisplayLabel";
import { resolveUsageEventDisplayCostMicrousd } from "./usageEventWorkUnits";
import { formatUsageEventTime, formatUsageUtcOffset } from "./usageDisplayUtils";
import { resolvePluginWorkspaceState } from '@/utils/pluginWorkspace';
import { getCurrentProductCode, isPluginProduct } from '@common/api/productContext';

interface WorkspaceUsageSummary {
  window_id: number;
  valid_from: string;
  valid_to: string;
  display_budget_microusd: number | string | null;
  display_used_microusd: number | string | null;
  display_remaining_microusd?: number | string | null;
  work_units_budget_display_microusd?: number | string | null;
  workUnitsBudgetDisplayMicrousd?: number | string | null;
  work_units_used_display_microusd?: number | string | null;
  workUnitsUsedDisplayMicrousd?: number | string | null;
  work_units_remaining_display_microusd?: number | string | null;
  workUnitsRemainingDisplayMicrousd?: number | string | null;
  usage_status?: UsageStatus | string | null;
  subscription_status?: string | null;
  reset_at?: string | null;
  status: "ACTIVE" | "INACTIVE" | "EXPIRED" | "CANCELLED";
  updated_at: string;
}

interface WorkspaceUsageEvent {
  usage_category?: CreditUsageEvent["usage_category"];
  usage_stage?: CreditUsageEvent["usage_stage"];
  usage_channel?: string | null;
  id: number | string;
  created_at: string;
  session_name?: string | null;
  session_user_email?: string | null;
  session_user_name?: string | null;
  capability: string | null;
  display_cost_microusd: number | string | null;
  work_units_display_microusd?: number | string | null;
  workUnitsDisplayMicrousd?: number | string | null;
  total_count?: number | string | null;
}

interface CreditsSummaryCard {
  id: "plan" | "topup";
  label: string;
  amount: string;
  amountSuffix?: string;
  secondaryAmount: string;
  footer: React.ReactNode;
  progressPct: number;
  progressColor: string;
  amountClassName?: string;
  tooltipText: React.ReactNode;
  statusLabel?: string;
  statusBadgeClass?: string;
  showBuyButton?: boolean;
  buyButtonDisabled?: boolean;
  buyButtonTitle?: string;
  buyButtonTooltip?: string;
  buyButtonAction?: "open_buy_modal";
  shopifyPendingPaymentTooltip?: string;
}

interface AutoReloadSettingsState {
  isEnabled: boolean;
  minimumBalance: number;
  targetBalance: number;
}

type CreditGrantStatus = "active" | "queued" | "exhausted";
type CreditGrantDisplayStatus = "in_use" | "available" | "used_up" | "expired";
type ExtraCreditsState =
  | "empty"
  | "available"
  | "frozen"
  | "expired"
  | "blocked";

interface CreditGrantView {
  id: string;
  purchasedAt: string;
  purchasedCreditsMicrousd: number;
  remainingCreditsMicrousd: number;
  expiresAt: string;
  status: CreditGrantStatus;
}

const Skeleton = ({ className }: { className?: string }) => (
  <div className={clsx("animate-pulse bg-gray-200 rounded", className)} />
);

const CreditsSummaryCardSkeleton = ({
  showStatusBadge = false,
  showActionButton = false,
  showProgressBar = true,
}: {
  showStatusBadge?: boolean;
  showActionButton?: boolean;
  showProgressBar?: boolean;
}) => {
  if (!showProgressBar) {
    return (
      <div className="rounded-2xl border border-[#E8EBF3] bg-white p-5 shadow-[0_10px_24px_rgba(26,22,47,0.06)] flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-8 w-24 rounded-full" />
          {showActionButton ? (
            <Skeleton className="h-6 w-6 rounded-full" />
          ) : null}
        </div>
        <Skeleton className="h-3 w-56 rounded-full" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#E8EBF3] bg-white p-5 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-28 rounded-full" />
          {showStatusBadge ? (
            <Skeleton className="h-5 w-14 rounded-full" />
          ) : null}
        </div>
        <div className="flex items-start gap-3">
          <div className="space-y-2 text-right">
            <Skeleton className="ml-auto h-8 w-28 rounded-full" />
          </div>
          {showActionButton ? (
            <Skeleton className="h-6 w-6 rounded-full" />
          ) : null}
        </div>
      </div>
      <Skeleton className="mb-3 h-2 w-full rounded-full" />
      <div className="space-y-2">
        <Skeleton className="h-3 w-44 rounded-full" />
        <Skeleton className="h-3 w-56 rounded-full" />
      </div>
    </div>
  );
};

const UsageRecordSkeleton = ({
  showUserColumn,
  tableGridTemplate,
  taskColumnOffsetClass,
  rows = 5,
}: {
  showUserColumn: boolean;
  tableGridTemplate: string;
  taskColumnOffsetClass: string;
  rows?: number;
}) => (
  <div className="overflow-hidden rounded-[20px] border border-[#E8EBF3] bg-white shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
    <div className="overflow-x-auto">
      <div className={clsx(showUserColumn ? "min-w-[840px]" : "min-w-[680px]")}>
    <div className="border-b border-[#ECEFF5] bg-[#FCFCFC] px-6 py-3">
      <div
        className="grid items-center gap-0"
        style={{ gridTemplateColumns: tableGridTemplate }}
      >
        <Skeleton
          className={clsx(taskColumnOffsetClass, "h-3 w-10 rounded-full")}
        />
        <Skeleton className="ml-5 h-3 w-10 rounded-full" />
        {showUserColumn ? <Skeleton className="h-3 w-10 rounded-full" /> : null}
        <Skeleton className="h-3 w-20 rounded-full" />
      </div>
    </div>

    <div className="divide-y divide-[#ECEFF5]">
      {Array.from({ length: rows }).map((_, idx) => (
        <div key={idx} className="px-6 py-4">
          <div
            className="grid items-center gap-0"
            style={{ gridTemplateColumns: tableGridTemplate }}
          >
            <div className={clsx(taskColumnOffsetClass, "min-w-0 pr-3")}>
              <Skeleton className="h-5 w-44 rounded-full" />
            </div>
            <div className="pl-5">
              <Skeleton className="h-5 w-24 rounded-full" />
              <Skeleton className="mt-2 h-3 w-16 rounded-full" />
            </div>
            {showUserColumn ? (
              <div className="min-w-0 pr-4">
                <Skeleton className="h-5 w-32 rounded-full" />
                <Skeleton className="mt-2 h-3 w-40 rounded-full" />
              </div>
            ) : null}
            <div>
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
      </div>
    </div>

    <div className="flex items-center justify-between border-t border-[#ECEFF5] bg-[#FCFCFC] px-6 py-4">
      <Skeleton className="h-4 w-40 rounded-full" />
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-8 rounded-lg" />
        <Skeleton className="h-8 w-8 rounded-lg" />
        <Skeleton className="h-8 w-8 rounded-lg" />
      </div>
    </div>
  </div>
);

const ITEMS_PER_PAGE = 10;
const MAX_VISIBLE_PAGE_BUTTONS = 5;
const OWNER_ADMIN_TABLE_GRID = "minmax(0, 0.88fr) 190px 205px 156px";
const MEMBER_TABLE_GRID = "minmax(0, 0.88fr) 190px 156px";
const MICROUSD_PER_CREDIT = 100_000;
const TOP_UP_CREDITS_PER_DOLLAR = 14;
const AUTO_RELOAD_STORAGE_KEY = "bustly:auto-reload-credit";
const AUTO_RELOAD_DEFAULT_SETTINGS: AutoReloadSettingsState = {
  isEnabled: false,
  minimumBalance: 125,
  targetBalance: 250,
};
const AUTO_RELOAD_ACTION_DELAY_MS = 700;
const SHOW_AUTO_RELOAD = false;
const TOPUP_RETURN_REFRESH_RETRY_DELAYS_MS = [0, 1500, 4000, 8000];
const CREDIT_TOPUP_SYNC_TIMEOUT_MS = 5_000;
const EXTRA_CREDITS_STATE_ALIASES: Record<string, ExtraCreditsState> = {
  empty: "empty",
  none: "empty",
  no_credits: "empty",
  no_extra_credits: "empty",
  available: "available",
  active: "available",
  usable: "available",
  frozen: "frozen",
  paused: "frozen",
  inactive: "frozen",
  expired: "expired",
  blocked: "blocked",
  purchase_blocked: "blocked",
};

const withCreditTopupSyncTimeout = async <T,>(
  promise: Promise<T>,
  timeoutMs: number = CREDIT_TOPUP_SYNC_TIMEOUT_MS,
): Promise<T> => {
  let timeoutId: number | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_resolve, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(
            new Error(`Credit top-up sync timed out after ${timeoutMs}ms`),
          );
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
};
const TOPUP_PERMISSION_REASONS = new Set([
  "missing_topup_permission",
  "not_workspace_member",
]);
const TOPUP_PLAN_BLOCK_REASONS = new Set([
  "free_plan",
  "trial_plan",
  "unsupported_plan",
  "plan_expired",
  "canceled_plan",
  "expired_plan",
  "past_due_plan",
]);

const getFirstNumberValue = (
  source: Record<string, unknown> | null | undefined,
  keys: string[],
) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value === null || value === undefined || value === "") continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const getFirstNumberValueFromSources = (
  sources: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
) => {
  for (const source of sources) {
    const value = getFirstNumberValue(source, keys);
    if (value !== null) return value;
  }
  return null;
};

const resolvePendingShopifyDailyCredits = (
  ...sources: Array<Record<string, unknown> | null | undefined>
) =>
  getFirstNumberValueFromSources(sources, [
    "pendingDailyCredits",
    "pending_daily_credits",
    "dailyPendingCredits",
    "daily_pending_credits",
    "dailyAllowanceCredits",
    "daily_allowance_credits",
    "planCreditDailyLimit",
    "plan_credit_daily_limit",
    "shopifyPendingDailyCredits",
    "shopify_pending_daily_credits",
  ]);

const getDisplayMicrousdFromBackendCredits = (
  sources: Array<Record<string, unknown> | null | undefined>,
  displayKeys: string[],
) => getFirstNumberValueFromSources(sources, displayKeys);

const resolveBackendPlanBudgetMicrousd = (
  sources: Array<Record<string, unknown> | null | undefined>,
) =>
  getDisplayMicrousdFromBackendCredits(
    sources,
    [
      "workUnitsBudgetDisplayMicrousd",
      "work_units_budget_display_microusd",
      "workUnitsBudgetMicrousd",
      "work_units_budget_microusd",
      "planDisplayBudgetMicrousd",
      "plan_display_budget_microusd",
      "monthlyDisplayBudgetMicrousd",
      "monthly_display_budget_microusd",
      "monthlyPlanDisplayBudgetMicrousd",
      "monthly_plan_display_budget_microusd",
      "planMonthlyDisplayBudgetMicrousd",
      "plan_monthly_display_budget_microusd",
      "displayPlanBudgetMicrousd",
      "display_plan_budget_microusd",
      "displayBudgetMicrousd",
      "display_budget_microusd",
    ],
  );

const resolveBackendPlanRemainingMicrousd = (
  sources: Array<Record<string, unknown> | null | undefined>,
) =>
  getDisplayMicrousdFromBackendCredits(
    sources,
    [
      "workUnitsRemainingDisplayMicrousd",
      "work_units_remaining_display_microusd",
      "workUnitsRemainingMicrousd",
      "work_units_remaining_microusd",
      "planDisplayRemainingMicrousd",
      "plan_display_remaining_microusd",
      "monthlyDisplayRemainingMicrousd",
      "monthly_display_remaining_microusd",
      "monthlyPlanDisplayRemainingMicrousd",
      "monthly_plan_display_remaining_microusd",
      "planMonthlyDisplayRemainingMicrousd",
      "plan_monthly_display_remaining_microusd",
      "displayPlanRemainingMicrousd",
      "display_plan_remaining_microusd",
    ],
  );

const resolveBackendPlanUsedMicrousd = (
  sources: Array<Record<string, unknown> | null | undefined>,
) =>
  getDisplayMicrousdFromBackendCredits(
    sources,
    [
      "workUnitsUsedDisplayMicrousd",
      "work_units_used_display_microusd",
      "workUnitsUsedMicrousd",
      "work_units_used_microusd",
      "planDisplayUsedMicrousd",
      "plan_display_used_microusd",
      "monthlyDisplayUsedMicrousd",
      "monthly_display_used_microusd",
      "monthlyPlanDisplayUsedMicrousd",
      "monthly_plan_display_used_microusd",
      "planMonthlyDisplayUsedMicrousd",
      "plan_monthly_display_used_microusd",
      "displayPlanUsedMicrousd",
      "display_plan_used_microusd",
    ],
  );

const formatIntegerForDisplay = (value: number, locale: LocaleCode = "en") => {
  const normalized = Number.isFinite(value) ? Math.trunc(value) : 0;
  return new Intl.NumberFormat(locale).format(Math.max(0, normalized));
};

const formatCreditsValue = (
  valueMicrousd?: number | string | null,
  locale: LocaleCode = "en",
) => {
  const credits = Number(valueMicrousd || 0) / MICROUSD_PER_CREDIT;
  if (!Number.isFinite(credits)) return "0.00";
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.max(0, credits));
};

const formatCreditsChange = (
  valueMicrousd?: number | string | null,
  locale: LocaleCode = "en",
) => {
  const rawCredits = Number(valueMicrousd || 0) / MICROUSD_PER_CREDIT;
  const credits = Math.abs(rawCredits);
  if (!Number.isFinite(credits) || credits <= 0) return "0.00";
  const prefix = rawCredits > 0 ? "+" : "-";
  if (credits < 0.01) return `${prefix}<0.01`;
  return `${prefix}${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(credits)}`;
};

const normalizeStatusToken = (value?: string | null) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const normalizeExtraCreditsState = (
  value?: string | null,
): ExtraCreditsState | null => {
  const normalized = normalizeStatusToken(value);
  if (!normalized || normalized === "live") return null;
  return EXTRA_CREDITS_STATE_ALIASES[normalized] || null;
};

const formatAutoReloadCost = (credits: number, locale: LocaleCode = "en") =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.max(0, credits) / TOP_UP_CREDITS_PER_DOLLAR);

const parseAutoReloadBalance = (value: string) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const parsedValue = Number(trimmedValue);
  if (!Number.isFinite(parsedValue) || parsedValue < 0) return null;

  return Math.trunc(parsedValue);
};

const readAutoReloadSettings = (): AutoReloadSettingsState => {
  if (typeof window === "undefined") {
    return { ...AUTO_RELOAD_DEFAULT_SETTINGS };
  }

  try {
    const rawValue = window.localStorage.getItem(AUTO_RELOAD_STORAGE_KEY);
    if (!rawValue) return { ...AUTO_RELOAD_DEFAULT_SETTINGS };

    const parsedValue = JSON.parse(
      rawValue,
    ) as Partial<AutoReloadSettingsState>;
    const minimumBalance = Number(parsedValue.minimumBalance);
    const targetBalance = Number(parsedValue.targetBalance);

    if (
      !Number.isFinite(minimumBalance) ||
      !Number.isFinite(targetBalance) ||
      targetBalance <= minimumBalance
    ) {
      return { ...AUTO_RELOAD_DEFAULT_SETTINGS };
    }

    return {
      isEnabled: Boolean(parsedValue.isEnabled),
      minimumBalance: Math.max(0, Math.trunc(minimumBalance)),
      targetBalance: Math.max(0, Math.trunc(targetBalance)),
    };
  } catch {
    return { ...AUTO_RELOAD_DEFAULT_SETTINGS };
  }
};

const getCreditsProgressColor = (remainingPct: number) => {
  if (remainingPct <= 0) return "#D1D7E6";
  return "#22C55E";
};

const formatPlanBadgeLabel = (value?: string | null) => {
  const normalized = (value || "").trim();
  if (!normalized) return "";

  return normalized
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

type PlanStatusBadge = UsageStatus | "trial";

const getPlanStatusLabel = (status: PlanStatusBadge, t: Translate) => {
  switch (status) {
    case "active":
      return t("website.usage.planStatusActive");
    case "trial":
      return t("website.usage.planStatusTrial");
    case "subscription_ending":
      return t("website.usage.planStatusEnding");
    case "expired":
      return t("website.usage.planStatusExpired");
    case "quota_reached":
      return t("website.usage.planStatusLimitReached");
    default:
      return "";
  }
};

const getPlanStatusBadgeClass = (status: PlanStatusBadge) => {
  switch (status) {
    case "active":
      return "bg-[#1A162F] text-white";
    case "trial":
      return "bg-amber-100 text-amber-700";
    case "subscription_ending":
    case "expired":
    case "quota_reached":
      return "bg-[#F4F5F8] text-[#666F8D]";
    default:
      return "bg-[#F4F5F8] text-[#666F8D]";
  }
};

const formatDateTime = (value?: string | null, locale: LocaleCode = "en") => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  if (locale === "zh-CN") {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hour = String(date.getHours()).padStart(2, "0");
    const minute = String(date.getMinutes()).padStart(2, "0");
    return `${year}/${month}/${day} ${hour}:${minute}`;
  }

  const month = new Intl.DateTimeFormat(locale, {
    month: "short",
  }).format(date);
  const day = date.getDate();
  const year = date.getFullYear();
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");

  return `${month} ${day}, ${year} at ${hour}:${minute}`;
};

const formatDateOnly = (value?: string | null, locale: LocaleCode = "en") => {
  if (!value) return "--";
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));
};

const getTaskName = (
  value: string | null | undefined,
  fallbackLabel: string,
) => {
  const task = (value || "").trim();
  return task || fallbackLabel;
};

const getUserEmail = (value?: string | null) => {
  const email = (value || "").trim();
  return email || "--";
};

const getUserName = (name?: string | null, email?: string | null) => {
  const trimmedName = (name || "").trim();
  if (trimmedName) return trimmedName;
  const trimmedEmail = (email || "").trim();
  if (!trimmedEmail) return "--";
  const prefix = trimmedEmail.split("@")[0]?.trim();
  return prefix || trimmedEmail;
};

const normalizeCreditSummary = (
  summary: CreditSummaryResponse,
): WorkspaceUsageSummary => {
  const monthly =
    summary.monthly || summary.currentWindow || summary.current_window || {};
  const monthlySources = [
    monthly as Record<string, unknown>,
    summary as unknown as Record<string, unknown>,
  ];
  const monthlyBudget =
    getDisplayMicrousdFromBackendCredits(
      monthlySources,
      [
        "workUnitsBudgetDisplayMicrousd",
        "work_units_budget_display_microusd",
        "displayBudgetMicrousd",
        "display_budget_microusd",
      ],
    ) ?? 0;
  const monthlyUsed =
    getDisplayMicrousdFromBackendCredits(
      monthlySources,
      [
        "workUnitsUsedDisplayMicrousd",
        "work_units_used_display_microusd",
        "displayUsedMicrousd",
        "display_used_microusd",
      ],
    ) ?? 0;
  const monthlyRemaining =
    getDisplayMicrousdFromBackendCredits(
      monthlySources,
      [
        "workUnitsRemainingDisplayMicrousd",
        "work_units_remaining_display_microusd",
        "displayRemainingMicrousd",
        "display_remaining_microusd",
      ],
    ) ?? Math.max(0, monthlyBudget - monthlyUsed);
  return {
    window_id: Number(monthly.windowId || summary.windowId || 0),
    valid_from: String(monthly.validFrom || summary.validFrom || ""),
    valid_to: String(monthly.validTo || summary.validTo || ""),
    display_budget_microusd: monthlyBudget,
    display_used_microusd: monthlyUsed,
    display_remaining_microusd: monthlyRemaining,
    workUnitsBudgetDisplayMicrousd: monthlyBudget,
    workUnitsUsedDisplayMicrousd: monthlyUsed,
    workUnitsRemainingDisplayMicrousd: monthlyRemaining,
    usage_status:
      summary.usageStatus ||
      summary.usage_status ||
      summary.eligibilityStatus ||
      summary.eligibility_status ||
      null,
    subscription_status: summary.status || null,
    reset_at:
      summary.resetAt || (monthly.validTo as string | undefined) || null,
    status:
      String(monthly.status || summary.status || "ACTIVE").toUpperCase() ===
      "EXPIRED"
        ? "EXPIRED"
        : "ACTIVE",
    updated_at: new Date().toISOString(),
  };
};

const getPurchasedSummaryDisplayMicrousd = (
  purchased: CreditSummaryResponse["purchased"] | null | undefined,
  displayKeys: string[],
) =>
  getDisplayMicrousdFromBackendCredits(
    [purchased as Record<string, unknown> | null | undefined],
    displayKeys,
  );

// 仅展示公开渠道；旧记录或未来渠道保留明确的未知状态。
const getUsageChannelLabel = (channel: string | null | undefined): string | null => {
  switch (channel) {
    case "google_search": case "google_trends": case "meta": case "tiktok":
    case "amazon": case "reddit": case "youtube": case "facebook":
    case "instagram": case "x":
      return platformLabel(channel as MarketPlatform);
    default:
      return null;
  }
};

const apiUsageEventToUsageEvent = (
  entry: CreditUsageEvent,
): WorkspaceUsageEvent => {
  return {
    usage_category: entry.usage_category,
    usage_stage: entry.usage_stage,
    usage_channel: entry.usage_channel,
    id: entry.id,
    created_at: entry.created_at,
    session_name: entry.session_name || null,
    session_user_email: entry.session_user_email || null,
    session_user_name: entry.session_user_name || null,
    capability: entry.capability || null,
    display_cost_microusd: resolveUsageEventDisplayCostMicrousd(
      entry as unknown as Record<string, unknown>,
    ),
    workUnitsDisplayMicrousd:
      entry.workUnitsDisplayMicrousd ?? entry.work_units_display_microusd,
    total_count: entry.total_count,
  };
};

const getGrantValue = (
  grant: ApiCreditGrant,
  snakeKey: keyof ApiCreditGrant,
  camelKey: keyof ApiCreditGrant,
) => grant[snakeKey] ?? grant[camelKey];

const getGrantDisplayMicrousd = (
  grant: ApiCreditGrant,
  displayKeys: string[],
) =>
  getFirstNumberValue(
    grant as unknown as Record<string, unknown>,
    displayKeys,
  ) ?? 0;

const creditGrantToView = (grant: ApiCreditGrant): CreditGrantView => {
  const validFrom = String(
    getGrantValue(grant, "valid_from", "validFrom") || "",
  );
  const createdAt = String(
    getGrantValue(grant, "created_at", "createdAt") || "",
  );
  const rawStatus = String(grant.status || "").toUpperCase();
  const startsInFuture = validFrom
    ? new Date(validFrom).getTime() > Date.now()
    : false;
  const purchasedCreditsMicrousd = getGrantDisplayMicrousd(grant, [
    "work_units_granted_display_microusd",
    "workUnitsGrantedDisplayMicrousd",
  ]);
  const remainingCreditsMicrousd = getGrantDisplayMicrousd(grant, [
    "work_units_remaining_display_microusd",
    "workUnitsRemainingDisplayMicrousd",
  ]);

  return {
    id: grant.id,
    purchasedAt: createdAt || validFrom,
    purchasedCreditsMicrousd,
    remainingCreditsMicrousd,
    expiresAt: String(getGrantValue(grant, "expires_at", "expiresAt") || ""),
    status:
      rawStatus === "ACTIVE" && remainingCreditsMicrousd > 0
        ? startsInFuture
          ? "queued"
          : "active"
        : "exhausted",
  };
};

const getCreditGrantUsedCreditsMicrousd = (grant: CreditGrantView) =>
  Math.max(
    0,
    Number(grant.purchasedCreditsMicrousd) -
      Number(grant.remainingCreditsMicrousd),
  );

const getCreditGrantDisplayStatus = (
  grant: CreditGrantView,
): CreditGrantDisplayStatus => {
  const remainingCredits = Math.max(
    0,
    Number(grant.remainingCreditsMicrousd || 0),
  );
  const expiresAt = new Date(grant.expiresAt).getTime();
  const isExpired =
    remainingCredits > 0 &&
    Number.isFinite(expiresAt) &&
    expiresAt <= Date.now();

  if (isExpired) return "expired";
  if (remainingCredits <= 0) return "used_up";
  const usedCredits = getCreditGrantUsedCreditsMicrousd(grant);
  if (usedCredits > 0) return "in_use";
  return "available";
};

const getCreditGrantStatusLabel = (
  status: CreditGrantDisplayStatus,
  t: Translate,
) => {
  switch (status) {
    case "in_use":
      return t("website.usage.grantStatusInUse");
    case "available":
      return t("website.usage.grantStatusAvailable");
    case "expired":
      return t("website.usage.grantStatusExpired");
    case "used_up":
    default:
      return t("website.usage.grantStatusUsedUp");
  }
};

const getCreditGrantStatusBadgeClass = (status: CreditGrantDisplayStatus) => {
  switch (status) {
    case "in_use":
      return "bg-green-50 text-green-700";
    case "available":
      return "bg-green-50 text-green-700";
    case "expired":
      return "bg-red-50 text-red-600";
    case "used_up":
    default:
      return "bg-[#F4F5F8] text-[#8A93B2]";
  }
};

const getCreditGrantSortPriority = (grant: CreditGrantView) => {
  switch (getCreditGrantDisplayStatus(grant)) {
    case "in_use":
      return 0;
    case "available":
      return 1;
    case "expired":
      return 2;
    case "used_up":
    default:
      return 3;
  }
};

const shouldShowCreditTopupReturnGlobalLoading = (topupReturn: {
  checked: boolean;
}) => !topupReturn.checked;

const PluginUsageSettings: React.FC = () => {
  const { t } = useTranslation();
  const { currentWorkspace, workspaces, permissions, isLoadingProfile, isLoadingWorkspaces } = useAuth();
  const resolvedWorkspace = currentWorkspace || workspaces[0] || null;
  const workspaceId = resolvedWorkspace?.id || null;
  const canViewPluginUsage = Boolean(workspaceId) && (permissions.isOwner || permissions.checkPermissions([Permission.USAGE_VIEW]));
  if (isLoadingProfile || (isLoadingWorkspaces && !resolvedWorkspace)) {
    return <div className="flex h-full items-center justify-center"><CircleNotch className="animate-spin text-[#8A93B2]" size={24} /></div>;
  }
  if (!canViewPluginUsage) {
    return <div className="flex h-full items-center justify-center px-8 text-center text-sm text-[#7D818A]">{t("website.usage.noPermissionViewUsageDetails")}</div>;
  }

  const planName = resolvedWorkspace?.subscription?.plan_name || resolvedWorkspace?.subscription?.plan_code || "Plugin";

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-[#FCFCFC] px-8 py-7">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[24px] font-semibold tracking-[-0.02em] text-[#1A162F]">{t("website.usage.usageRecord")}</h2>
          <p className="mt-1 text-sm text-[#7D818A]">{planName}</p>
        </div>
      </div>
      <div className="rounded-2xl border border-[#E8EBF3] bg-white p-6 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
        <h3 className="font-medium text-[#1A162F]">{t("website.usage.pluginMvpTitle")}</h3>
        <p className="mt-2 text-sm leading-6 text-[#7D818A]">{t("website.usage.pluginMvpDescription")}</p>
      </div>
    </div>
  );
};

const CoreUsageSettings: React.FC = () => {
  const { locale, t } = useTranslation();
  const {
    currentWorkspace,
    workspaces,
    userBilling,
    isLoadingProfile,
    isLoadingWorkspaces,
    permissions,
    showGlobalLoading,
    hideGlobalLoading,
  } = useAuth();
  const { modal } = useAdminContext();
  const { showToast } = useToast();
  const resolvedWorkspace = currentWorkspace || workspaces[0] || null;
  const workspaceId = resolvedWorkspace?.id || null;
  const canViewCredits = Boolean(workspaceId);
  const canViewUsage =
    Boolean(workspaceId) &&
    (permissions.isOwner ||
      permissions.checkPermissions([Permission.USAGE_VIEW]));
  const isWorkspaceContextLoading =
    isLoadingProfile || (isLoadingWorkspaces && !resolvedWorkspace);
  const isAccessEvaluating =
    isWorkspaceContextLoading ||
    (Boolean(workspaceId) && permissions.isLoading);
  const showWorkspaceSummary = canViewCredits;
  const showUserColumn = canViewUsage;

  const [usageSummary, setUsageSummary] =
    useState<WorkspaceUsageSummary | null>(null);
  const [creditSummary, setCreditSummary] =
    useState<CreditSummaryResponse | null>(null);
  const [creditGrants, setCreditGrants] = useState<CreditGrantView[]>([]);
  const [topupSkus, setTopupSkus] = useState<CreditTopupSku[]>([]);
  const [topupEligibility, setTopupEligibility] =
    useState<CreditTopupEligibilityResponse | null>(null);
  const [isLoadingTopupOptions, setIsLoadingTopupOptions] = useState(false);
  const [topupOptionsError, setTopupOptionsError] = useState<string | null>(
    null,
  );
  const pluginWorkspace = resolvePluginWorkspaceState({
    currentWorkspacePlanCode: resolvedWorkspace?.subscription?.plan_code,
    currentWorkspacePlanName: resolvedWorkspace?.subscription?.plan_name,
    currentWorkspacePlanProvider: resolvedWorkspace?.subscription?.provider,
    billingSummaryPlanCode: userBilling.summary?.planCode,
    billingSummaryPlanName: userBilling.summary?.planName,
    billingSummaryProvider:
      userBilling.summary?.provider || userBilling.summary?.billingProvider,
    creditSummaryPlanCode: creditSummary?.subscription?.planCode,
    creditSummaryPlanName: creditSummary?.subscription?.planName,
    creditSummaryProvider:
      creditSummary?.subscription?.provider ||
      creditSummary?.subscription?.billingProvider,
    userPlanCode: userBilling.userPlan?.planCode,
    userPlanName: userBilling.userPlan?.planName,
    userPlanProvider: userBilling.userPlan?.provider,
  });
  const shouldUsePluginWorkspaceUi = isPluginProduct(getCurrentProductCode());
  const hasResolvedTopupEligibility = Boolean(
    shouldUsePluginWorkspaceUi || topupEligibility || topupOptionsError,
  );
  const topupOptionsRequestIdRef = useRef(0);
  const supportsUsageCategories = getAdminProductFeatures().marketIntelligence;
  const [usageCategory, setUsageCategory] = useState<"client" | "market_intelligence">("client");
  const [usageEventsError, setUsageEventsError] = useState(false);
  const [usageEventsRetry, setUsageEventsRetry] = useState(0);
  const [events, setEvents] = useState<WorkspaceUsageEvent[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingWindow, setIsLoadingWindow] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [hasResolvedInitialEvents, setHasResolvedInitialEvents] =
    useState(false);
  const [taskTooltip, setTaskTooltip] = useState<{
    left: number;
    top: number;
    text: string;
  } | null>(null);
  const [progressTooltip, setProgressTooltip] = useState<{
    left: number;
    top: number;
    text: React.ReactNode;
    align?: "center" | "right";
    placement?: "top" | "bottom";
  } | null>(null);
  const [buyButtonTooltip, setBuyButtonTooltip] = useState<{
    left: number;
    top: number;
    text: string;
  } | null>(null);
  const [isBuyMoreModalOpen, setIsBuyMoreModalOpen] = useState(false);
  const [isGrantsModalOpen, setIsGrantsModalOpen] = useState(false);
  const [topupReturnRefreshKey, setTopupReturnRefreshKey] = useState(0);
  const hasHandledTopupReturnRef = useRef(false);
  const topupReturnRefreshTimeoutsRef = useRef<number[]>([]);
  const [grantsPage, setGrantsPage] = useState(1);
  const grantsPerPage = 5;

  const sortedCreditGrants = useMemo(() => {
    return [...creditGrants].sort((left, right) => {
      const priorityDifference =
        getCreditGrantSortPriority(left) - getCreditGrantSortPriority(right);
      if (priorityDifference !== 0) return priorityDifference;

      return (
        new Date(left.expiresAt).getTime() - new Date(right.expiresAt).getTime()
      );
    });
  }, [creditGrants]);
  const paginatedGrants = useMemo(() => {
    return sortedCreditGrants.slice(
      (grantsPage - 1) * grantsPerPage,
      grantsPage * grantsPerPage,
    );
  }, [grantsPage, sortedCreditGrants]);
  const totalGrantsPages = Math.ceil(sortedCreditGrants.length / grantsPerPage);
  const visibleGrantPages = useMemo(() => {
    if (totalGrantsPages <= MAX_VISIBLE_PAGE_BUTTONS) {
      return Array.from({ length: totalGrantsPages }, (_, idx) => idx + 1);
    }

    const half = Math.floor(MAX_VISIBLE_PAGE_BUTTONS / 2);
    let start = Math.max(1, grantsPage - half);
    let end = start + MAX_VISIBLE_PAGE_BUTTONS - 1;

    if (end > totalGrantsPages) {
      end = totalGrantsPages;
      start = end - MAX_VISIBLE_PAGE_BUTTONS + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  }, [grantsPage, totalGrantsPages]);

  const [autoReloadSettings, setAutoReloadSettings] =
    useState<AutoReloadSettingsState>(readAutoReloadSettings);

  useEffect(() => {
    if (!isGrantsModalOpen) {
      setGrantsPage(1);
    }
  }, [isGrantsModalOpen]);
  const [isAutoReloadModalOpen, setIsAutoReloadModalOpen] = useState(false);
  const [autoReloadDraft, setAutoReloadDraft] = useState(() => ({
    minimumBalance: String(AUTO_RELOAD_DEFAULT_SETTINGS.minimumBalance),
    targetBalance: String(AUTO_RELOAD_DEFAULT_SETTINGS.targetBalance),
  }));
  const [autoReloadPendingAction, setAutoReloadPendingAction] = useState<
    "turn_on" | "turn_off" | null
  >(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [workspaceId]);

  useEffect(() => {
    setHasResolvedInitialEvents(false);
  }, [workspaceId]);

  const loadCreditTopupOptions = useCallback(async () => {
    if (!workspaceId || !canViewCredits || shouldUsePluginWorkspaceUi) {
      topupOptionsRequestIdRef.current += 1;
      setTopupSkus([]);
      setTopupEligibility(null);
      setTopupOptionsError(null);
      setIsLoadingTopupOptions(false);
      return;
    }

    const requestId = topupOptionsRequestIdRef.current + 1;
    topupOptionsRequestIdRef.current = requestId;
    setIsLoadingTopupOptions(true);
    setTopupEligibility(null);
    setTopupOptionsError(null);
    try {
      const [nextSkus, nextEligibility] = await Promise.all([
        billingService.getCreditTopupSkus(),
        billingService.getCreditTopupEligibility(),
      ]);
      if (requestId !== topupOptionsRequestIdRef.current) return;
      setTopupSkus(nextSkus);
      setTopupEligibility(nextEligibility);
    } catch (error: any) {
      if (requestId !== topupOptionsRequestIdRef.current) return;
      console.error("Failed to fetch credit top-up options:", error);
      setTopupSkus([]);
      setTopupEligibility(null);
      setTopupOptionsError(
        error?.message || t("website.usage.failedToLoadCreditOptions"),
      );
    } finally {
      if (requestId === topupOptionsRequestIdRef.current) {
        setIsLoadingTopupOptions(false);
      }
    }
  }, [canViewCredits, shouldUsePluginWorkspaceUi, workspaceId]);

  useEffect(() => {
    loadCreditTopupOptions();
  }, [loadCreditTopupOptions]);

  const clearTopupReturnRefreshes = useCallback(() => {
    topupReturnRefreshTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    topupReturnRefreshTimeoutsRef.current = [];
  }, []);

  const refreshTopupDataAfterReturn = useCallback(() => {
    setCurrentPage(1);
    setTopupReturnRefreshKey((current) => current + 1);
  }, []);

  const scheduleTopupReturnRefreshes = useCallback(() => {
    if (typeof window === "undefined") {
      refreshTopupDataAfterReturn();
      return;
    }

    clearTopupReturnRefreshes();
    topupReturnRefreshTimeoutsRef.current =
      TOPUP_RETURN_REFRESH_RETRY_DELAYS_MS.map((delayMs) =>
        window.setTimeout(() => {
          refreshTopupDataAfterReturn();
        }, delayMs),
      );
  }, [clearTopupReturnRefreshes, refreshTopupDataAfterReturn]);

  useEffect(() => {
    return () => {
      clearTopupReturnRefreshes();
    };
  }, [clearTopupReturnRefreshes]);

  useEffect(() => {
    if (typeof window === "undefined" || hasHandledTopupReturnRef.current) {
      return;
    }

    const topupReturn = readCreditTopupReturnState(
      new URLSearchParams(window.location.search),
      readPendingCreditTopupCheckoutSessionId(),
    );
    if (!topupReturn.result) {
      return;
    }
    if (
      isWorkspaceContextLoading ||
      !canViewCredits ||
      (topupReturn.workspaceId && topupReturn.workspaceId !== workspaceId)
    ) {
      return;
    }

    hasHandledTopupReturnRef.current = true;
    setIsBuyMoreModalOpen(false);
    console.info("[UsageSettings] Credit top-up return detected", {
      route_kind: "settings_usage",
      current_state: "topup_return",
      transition_reason: topupReturn.result,
      workspace_id_present: Boolean(topupReturn.workspaceId),
      session_id_present: Boolean(topupReturn.sessionId),
    });

    const clearedReturnUrl = buildCreditTopupReturnClearedUrl(
      window.location.href,
    );

    const clearTopupReturnParams = () => {
      window.history.replaceState({}, "", clearedReturnUrl);
    };

    if (topupReturn.result !== "success") {
      clearPendingCreditTopupCheckoutSessionId();
      clearTopupReturnParams();
      return;
    }

    const shouldShowTopupReturnGlobalLoading =
      shouldShowCreditTopupReturnGlobalLoading(topupReturn);
    if (shouldShowTopupReturnGlobalLoading) {
      showGlobalLoading();
    }
    const handleSuccessfulTopupReturn = async () => {
      try {
        if (topupReturn.checked) {
          console.info("[UsageSettings] Credit top-up return already checked", {
            route_kind: "settings_usage",
            current_state: "topup_return",
            transition_reason: "checked_payment_return",
            workspace_id_present: Boolean(topupReturn.workspaceId),
            session_id_present: Boolean(topupReturn.sessionId),
          });
        } else if (topupReturn.sessionId) {
          await withCreditTopupSyncTimeout(
            billingService.syncCreditTopupCheckoutSession({
              sessionId: topupReturn.sessionId,
            }),
          );
        } else {
          console.warn(
            "[UsageSettings] Credit top-up return missing checkout session id",
            {
              route_kind: "settings_usage",
              current_state: "topup_return",
              transition_reason: "missing_session_id",
              workspace_id_present: Boolean(topupReturn.workspaceId),
            },
          );
        }
        if (topupReturn.checked) {
          refreshTopupDataAfterReturn();
        } else {
          scheduleTopupReturnRefreshes();
        }
        modal.openModal("paymentSuccess", {
          data: { kind: "credits" },
        });
        void loadCreditTopupOptions();
      } catch (error) {
        console.error("Failed to sync credit top-up checkout:", error);
        if (topupReturn.checked) {
          refreshTopupDataAfterReturn();
        } else {
          scheduleTopupReturnRefreshes();
        }
        void loadCreditTopupOptions();
      } finally {
        clearPendingCreditTopupCheckoutSessionId();
        if (shouldShowTopupReturnGlobalLoading) {
          hideGlobalLoading();
        }
        clearTopupReturnParams();
      }
    };

    void handleSuccessfulTopupReturn();
  }, [
    canViewCredits,
    hideGlobalLoading,
    isWorkspaceContextLoading,
    loadCreditTopupOptions,
    modal,
    refreshTopupDataAfterReturn,
    scheduleTopupReturnRefreshes,
    showToast,
    showGlobalLoading,
    t,
    workspaceId,
  ]);

  useEffect(() => {
    if (!workspaceId || !canViewCredits) {
      setUsageSummary(null);
      setCreditSummary(null);
      setCreditGrants([]);
      setIsLoadingWindow(false);
      return;
    }

    let isActive = true;
    const shouldRefresh = topupReturnRefreshKey > 0;

    const fetchUsageSummary = async () => {
      setIsLoadingWindow(true);
      try {
        const data = await billingService.getCreditSummary(
          shouldRefresh ? { refresh: true } : undefined,
        );
        if (!isActive) return;
        setCreditSummary(data);
        setUsageSummary(normalizeCreditSummary(data));
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to fetch workspace usage summary:", error);
        setCreditSummary(null);
        setUsageSummary(null);
      } finally {
        if (isActive) {
          setIsLoadingWindow(false);
        }
      }
    };

    fetchUsageSummary();

    return () => {
      isActive = false;
    };
  }, [workspaceId, canViewCredits, topupReturnRefreshKey]);

  useEffect(() => {
    if (!workspaceId || !canViewCredits) {
      setCreditGrants([]);
      return;
    }

    let isActive = true;
    const shouldRefresh = topupReturnRefreshKey > 0;

    const fetchCreditGrants = async () => {
      try {
        const data = await billingService.getCreditGrants({
          page: 1,
          size: 100,
          ...(shouldRefresh ? { refresh: true } : {}),
        });
        if (!isActive) return;
        setCreditGrants((data.grants || []).map(creditGrantToView));
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to fetch purchased credit packages:", error);
        setCreditGrants([]);
      }
    };

    fetchCreditGrants();

    return () => {
      isActive = false;
    };
  }, [workspaceId, canViewCredits, topupReturnRefreshKey]);

  useEffect(() => {
    if (!workspaceId || !canViewUsage || (supportsUsageCategories && usageCategory === "market_intelligence")) {
      setEvents([]);
      setTotalEvents(0);
      setIsLoadingEvents(false);
      return;
    }

    let isActive = true;
    const shouldRefresh = topupReturnRefreshKey > 0;

    const fetchEvents = async () => {
      setIsLoadingEvents(true);
      setUsageEventsError(false);
      try {
        const data = await billingService.getCreditUsageEvents({
          page: currentPage,
          size: ITEMS_PER_PAGE,
          ...(supportsUsageCategories ? { usage_category: usageCategory } : {}),
          ...(shouldRefresh ? { refresh: true } : {}),
        });
        if (!isActive) return;
        const nextEntries = data.events || [];
        if (supportsUsageCategories && nextEntries.some(entry => entry.usage_category !== usageCategory)) {
          throw new Error("Usage category response mismatch");
        }
        const nextEvents = nextEntries.map(apiUsageEventToUsageEvent);
        setEvents(nextEvents);
        setTotalEvents(
          Number(data.pagination?.total || nextEvents.length || 0),
        );
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to fetch workspace billing events:", error);
        setUsageEventsError(true);
        setEvents([]);
        setTotalEvents(0);
      } finally {
        if (isActive) {
          setHasResolvedInitialEvents(true);
          setIsLoadingEvents(false);
        }
      }
    };

    fetchEvents();

    return () => {
      isActive = false;
    };
  }, [workspaceId, currentPage, canViewUsage, topupReturnRefreshKey, supportsUsageCategories, usageCategory, usageEventsRetry]);

  useEffect(() => {
    if (!taskTooltip) return undefined;

    const hideTaskTooltip = () => setTaskTooltip(null);

    window.addEventListener("resize", hideTaskTooltip);
    window.addEventListener("scroll", hideTaskTooltip, true);

    return () => {
      window.removeEventListener("resize", hideTaskTooltip);
      window.removeEventListener("scroll", hideTaskTooltip, true);
    };
  }, [taskTooltip]);

  useEffect(() => {
    if (!progressTooltip) return undefined;

    const hideProgressTooltip = () => setProgressTooltip(null);

    window.addEventListener("resize", hideProgressTooltip);
    window.addEventListener("scroll", hideProgressTooltip, true);

    return () => {
      window.removeEventListener("resize", hideProgressTooltip);
      window.removeEventListener("scroll", hideProgressTooltip, true);
    };
  }, [progressTooltip]);

  useEffect(() => {
    if (!buyButtonTooltip) return undefined;

    const hideBuyButtonTooltip = () => setBuyButtonTooltip(null);

    window.addEventListener("resize", hideBuyButtonTooltip);
    window.addEventListener("scroll", hideBuyButtonTooltip, true);

    return () => {
      window.removeEventListener("resize", hideBuyButtonTooltip);
      window.removeEventListener("scroll", hideBuyButtonTooltip, true);
    };
  }, [buyButtonTooltip]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(
        AUTO_RELOAD_STORAGE_KEY,
        JSON.stringify(autoReloadSettings),
      );
    } catch {
      // Ignore storage failures and keep the UI responsive.
    }
  }, [autoReloadSettings]);

  const showTaskTooltip = (
    e: React.MouseEvent<HTMLSpanElement> | React.FocusEvent<HTMLSpanElement>,
    taskName: string,
  ) => {
    const el = e.currentTarget;
    // Only show tooltip when text is actually truncated.
    if (el.scrollWidth <= el.clientWidth) return;
    const rect = el.getBoundingClientRect();
    setTaskTooltip({
      text: taskName,
      left: rect.left + rect.width / 2,
      top: rect.top,
    });
  };

  const totalPages = Math.max(1, Math.ceil(totalEvents / ITEMS_PER_PAGE));
  const visiblePages = useMemo(() => {
    if (totalPages <= MAX_VISIBLE_PAGE_BUTTONS) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const half = Math.floor(MAX_VISIBLE_PAGE_BUTTONS / 2);
    let start = Math.max(1, currentPage - half);
    let end = start + MAX_VISIBLE_PAGE_BUTTONS - 1;

    if (end > totalPages) {
      end = totalPages;
      start = end - MAX_VISIBLE_PAGE_BUTTONS + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  }, [currentPage, totalPages]);

  const usageStatus = useMemo<UsageStatus>(() => {
    return resolveUsageStatus({
      rawUsageStatus: usageSummary?.usage_status,
      usageSummaryStatus: usageSummary?.status,
      displayBudgetMicrousd: usageSummary?.display_budget_microusd,
      displayUsedMicrousd: usageSummary?.display_used_microusd,
      isBillingExpired: userBilling.isExpired,
      isBillingCanceled: userBilling.isCanceled,
    });
  }, [usageSummary, userBilling.isExpired, userBilling.isCanceled]);
  const localUtcOffset = useMemo(() => formatUsageUtcOffset(), []);
  const isTrial = Boolean(userBilling.isTrial);
  const planCreditSignalSources = useMemo(
    () =>
      [
        creditSummary as Record<string, unknown> | null,
        creditSummary?.planEntitlement as Record<string, unknown> | null,
        creditSummary?.plan_entitlement as Record<string, unknown> | null,
        creditSummary?.currentWindow as Record<string, unknown> | null,
        creditSummary?.current_window as Record<string, unknown> | null,
        userBilling.summary as Record<string, unknown> | null,
        userBilling.userPlan as unknown as Record<string, unknown> | null,
      ].filter(Boolean),
    [creditSummary, userBilling.summary, userBilling.userPlan],
  );
  const planCreditRefreshState = useMemo(
    () =>
      resolveUsagePlanCreditRefreshState({
        isTrial,
        creditSummary,
        subscriptionSummary: userBilling.summary,
        workspacePlan: userBilling.userPlan as any,
        billingProvider: userBilling.userPlan?.provider,
      }),
    [
      creditSummary,
      isTrial,
      userBilling.summary,
      userBilling.userPlan,
      userBilling.userPlan?.provider,
    ],
  );
  const pendingShopifyDailyCredits = useMemo(
    () => resolvePendingShopifyDailyCredits(...planCreditSignalSources),
    [planCreditSignalSources],
  );
  const hasShopifyPendingPaymentNotice = useMemo(
    () =>
      resolveShopifyPendingPaymentNotice({
        isTrial,
        creditSummary,
        subscriptionSummary: userBilling.summary,
        workspacePlan: userBilling.userPlan as any,
        billingProvider: userBilling.userPlan?.provider,
      }),
    [
      creditSummary,
      isTrial,
      userBilling.summary,
      userBilling.userPlan,
      userBilling.userPlan?.provider,
    ],
  );
  const usageTrackLabel =
    planCreditRefreshState === "trial_daily"
      ? t("website.usage.dailyCredits")
      : t("website.usage.monthlyCredits");
  const planFooterTrackLabel = usageTrackLabel;
  const planResetText = t("website.usage.resetsOn", {
    date: formatDateTime(
      usageSummary?.reset_at || usageSummary?.valid_to,
      locale,
    ),
    timezone: localUtcOffset,
  });
  const shopifyPendingPaymentTooltip = hasShopifyPendingPaymentNotice
    ? pendingShopifyDailyCredits !== null
      ? t("website.usage.shopifyPendingPaymentTooltip", {
          dailyCredits: formatIntegerForDisplay(
            pendingShopifyDailyCredits,
            locale,
          ),
        })
      : t("website.usage.shopifyPendingPaymentTooltipWithoutLimit")
    : "";

  const previewStates = useMemo(() => {
    const budgetMicrousd = Number(usageSummary?.display_budget_microusd || 0);
    const usedMicrousd = Number(usageSummary?.display_used_microusd || 0);
    const remainingMicrousd = Number(
      usageSummary?.display_remaining_microusd ??
        Math.max(0, budgetMicrousd - usedMicrousd),
    );
    const backendPlanSources = [
      creditSummary as Record<string, unknown> | null,
      creditSummary?.planEntitlement as Record<string, unknown> | null,
      creditSummary?.plan_entitlement as Record<string, unknown> | null,
      usageSummary as unknown as Record<string, unknown> | null,
      userBilling.summary as Record<string, unknown> | null,
      userBilling.userPlan as unknown as Record<string, unknown> | null,
    ];
    const backendPlanBudgetMicrousd =
      resolveBackendPlanBudgetMicrousd(backendPlanSources);
    const backendPlanRemainingMicrousd =
      resolveBackendPlanRemainingMicrousd(backendPlanSources);
    const backendPlanUsedMicrousd =
      resolveBackendPlanUsedMicrousd(backendPlanSources);
    const displayBudgetMicrousd =
      hasShopifyPendingPaymentNotice && backendPlanBudgetMicrousd !== null
        ? backendPlanBudgetMicrousd
        : budgetMicrousd;
    const displayRemainingMicrousd = hasShopifyPendingPaymentNotice
      ? (backendPlanRemainingMicrousd ??
        backendPlanBudgetMicrousd ??
        remainingMicrousd)
      : remainingMicrousd;
    const displayUsedMicrousd = hasShopifyPendingPaymentNotice
      ? (backendPlanUsedMicrousd ??
        (backendPlanBudgetMicrousd !== null
          ? Math.max(0, backendPlanBudgetMicrousd - displayRemainingMicrousd)
          : usedMicrousd))
      : usedMicrousd;
    // Remaining percentage: 100 = all credits available, 0 = fully used
    const remainingPct =
      displayBudgetMicrousd > 0
        ? Math.max(
            0,
            Math.min(
              100,
              Math.round(
                (displayRemainingMicrousd / displayBudgetMicrousd) * 100,
              ),
            ),
          )
        : 0;

    const shared = {
      amount: formatCreditsValue(displayRemainingMicrousd, locale),
      total: formatCreditsValue(displayBudgetMicrousd, locale),
      usedAmount: formatCreditsValue(displayUsedMicrousd, locale),
      remainingPct,
    };

    return {
      active: {
        ...shared,
        footerRight: planResetText,
      },
      subscription_ending: {
        ...shared,
        footerRight: t("website.usage.endsOn", {
          date: formatDateOnly(
            userBilling.summary?.expiresAt ||
              userBilling.userPlan?.currentPeriodEnd ||
              usageSummary?.valid_to,
            locale,
          ),
        }),
      },
      expired: {
        amount: "0",
        total: shared.total,
        usedAmount: shared.usedAmount,
        remainingPct: 0,
        footerRight: isTrial
          ? t("website.usage.startSubscriptionToContinue")
          : t("website.usage.upgradeToContinue"),
      },
      quota_reached: {
        amount: formatCreditsValue(displayRemainingMicrousd, locale),
        total: formatCreditsValue(
          displayBudgetMicrousd || displayUsedMicrousd,
          locale,
        ),
        remainingPct: hasShopifyPendingPaymentNotice ? remainingPct : 0,
        footerRight: hasShopifyPendingPaymentNotice
          ? planResetText
          : t("website.usage.upgradeToContinue"),
      },
    };
  }, [
    hasShopifyPendingPaymentNotice,
    isTrial,
    locale,
    planResetText,
    t,
    usageSummary,
    creditSummary,
    userBilling.summary?.expiresAt,
    userBilling.summary,
    userBilling.userPlan?.currentPeriodEnd,
    userBilling.userPlan,
  ]);

  const currentPreview = previewStates[usageStatus];
  const planStatusBadge = useMemo<PlanStatusBadge>(() => {
    if (isTrial && usageStatus === "active") return "trial";
    if (usageStatus === "subscription_ending") return "active";
    return usageStatus;
  }, [usageStatus, isTrial]);
  const planDisplayLabel = useMemo(() => {
    return resolveUsagePlanDisplayLabel({
      summaryPlanName: userBilling.summary?.planName,
      userPlanName: userBilling.userPlan?.planName,
      planDisplayText: userBilling.planText?.planDisplayText,
      fallbackLabel: usageTrackLabel,
    });
  }, [
    usageTrackLabel,
    userBilling.planText?.planDisplayText,
    userBilling.summary?.planName,
    userBilling.userPlan?.planName,
  ]);
  const planSummaryCard = useMemo<CreditsSummaryCard>(
    () => ({
      id: "plan",
      label: planDisplayLabel,
      amount: currentPreview.amount,
      amountSuffix: t("website.usage.amountRemainingSuffix"),
      secondaryAmount: "",
      footer: `${planFooterTrackLabel} · ${currentPreview.footerRight}`,
      progressPct: currentPreview.remainingPct,
      progressColor: getCreditsProgressColor(currentPreview.remainingPct),
      tooltipText: `${currentPreview.amount}/${currentPreview.total} ${usageTrackLabel}`,
      statusLabel: getPlanStatusLabel(planStatusBadge, t),
      statusBadgeClass: getPlanStatusBadgeClass(planStatusBadge),
      shopifyPendingPaymentTooltip:
        hasShopifyPendingPaymentNotice && shopifyPendingPaymentTooltip
          ? shopifyPendingPaymentTooltip
          : undefined,
    }),
    [
      currentPreview.amount,
      currentPreview.footerRight,
      currentPreview.remainingPct,
      currentPreview.total,
      hasShopifyPendingPaymentNotice,
      planDisplayLabel,
      planFooterTrackLabel,
      planStatusBadge,
      shopifyPendingPaymentTooltip,
      t,
      usageTrackLabel,
    ],
  );

  const topUpSummaryCard = useMemo<CreditsSummaryCard>(() => {
    const purchased = creditSummary?.purchased || null;
    const topUpRemainingMicrousd =
      getPurchasedSummaryDisplayMicrousd(
        purchased,
        [
          "workUnitsRemainingDisplayMicrousd",
          "work_units_remaining_display_microusd",
          "displayRemainingMicrousd",
          "display_remaining_microusd",
        ],
      ) ?? 0;
    const topUpTotalMicrousd =
      getPurchasedSummaryDisplayMicrousd(
        purchased,
        [
          "workUnitsBudgetDisplayMicrousd",
          "work_units_budget_display_microusd",
          "displayBudgetMicrousd",
          "display_budget_microusd",
        ],
      ) ?? 0;
    const availableTopUpGrants = sortedCreditGrants.filter((grant) => {
      const status = getCreditGrantDisplayStatus(grant);
      return status === "in_use" || status === "available";
    });
    const availableTopUpRemainingMicrousd = availableTopUpGrants.reduce(
      (total, grant) =>
        total + Math.max(0, Number(grant.remainingCreditsMicrousd || 0)),
      0,
    );
    const availableTopUpTotalMicrousd = availableTopUpGrants.reduce(
      (total, grant) =>
        total + Math.max(0, Number(grant.purchasedCreditsMicrousd || 0)),
      0,
    );
    const hasAvailableTopUpCredits = availableTopUpGrants.length > 0;
    const hasTopUpData = Boolean(purchased) || sortedCreditGrants.length > 0;
    const normalizedRemaining = Math.max(
      0,
      Number(
        hasAvailableTopUpCredits
          ? availableTopUpRemainingMicrousd
          : topUpRemainingMicrousd || 0,
      ),
    );
    const normalizedTotal = Math.max(
      normalizedRemaining,
      Number(
        hasAvailableTopUpCredits
          ? availableTopUpTotalMicrousd
          : topUpTotalMicrousd || 0,
      ),
    );
    const remainingPct =
      hasTopUpData && normalizedTotal > 0
        ? Math.max(
            0,
            Math.min(
              100,
              Math.round((normalizedRemaining / normalizedTotal) * 100),
            ),
          )
        : 0;

    const topUpGrantStatuses = sortedCreditGrants.map((grant) =>
      getCreditGrantDisplayStatus(grant),
    );
    const hasExpiredGrants = topUpGrantStatuses.some(
      (status) => status === "expired",
    );
    const hasOnlyInactiveTopUpGrants =
      topUpGrantStatuses.length > 0 &&
      topUpGrantStatuses.every(
        (status) => status === "expired" || status === "used_up",
      );
    const summaryReasonCode = normalizeStatusToken(
      creditSummary?.reasonCode || creditSummary?.reason_code,
    );
    const eligibilityReasonCode = normalizeStatusToken(
      topupEligibility?.reasonCode,
    );
    const topupReasonCode = eligibilityReasonCode || summaryReasonCode;
    const isPurchasePermissionDenied =
      TOPUP_PERMISSION_REASONS.has(topupReasonCode);
    const isPlanBlockedForExtraCredits =
      isTrial ||
      usageStatus === "expired" ||
      TOPUP_PLAN_BLOCK_REASONS.has(topupReasonCode);
    const canBuyTopup =
      creditSummary?.canBuyTopup ?? creditSummary?.can_buy_topup;
    const isTopupPurchaseUnavailable =
      canBuyTopup === false || topupEligibility?.eligible === false;
    const isPurchaseBlockedByPlan =
      isPlanBlockedForExtraCredits ||
      (isTopupPurchaseUnavailable && !isPurchasePermissionDenied);
    const rawBackendExtraCreditsState = normalizeExtraCreditsState(
      creditSummary?.extraCreditsState || creditSummary?.extra_credits_state,
    );
    const backendExtraCreditsState =
      isPurchasePermissionDenied &&
      !isPlanBlockedForExtraCredits &&
      (rawBackendExtraCreditsState === "frozen" ||
        rawBackendExtraCreditsState === "blocked")
        ? null
        : rawBackendExtraCreditsState;
    const fallbackExtraCreditsState: ExtraCreditsState =
      hasExpiredGrants && hasOnlyInactiveTopUpGrants
        ? "expired"
        : normalizedRemaining > 0
          ? isPlanBlockedForExtraCredits
            ? "frozen"
            : "available"
          : isPurchaseBlockedByPlan
            ? "blocked"
            : "empty";
    const extraCreditsState =
      backendExtraCreditsState || fallbackExtraCreditsState;

    let statusLabel: string | undefined;
    let statusBadgeClass: string | undefined;
    let footer = t("website.usage.usePurchasedCreditsDescription");
    let amountClassName: string | undefined;
    const showBuyButton = hasResolvedTopupEligibility && !topupOptionsError;
    const buyButtonDisabled =
      showBuyButton &&
      (isPurchasePermissionDenied ||
        isPurchaseBlockedByPlan ||
        extraCreditsState === "blocked" ||
        extraCreditsState === "frozen");
    const buyButtonTooltip = isPurchasePermissionDenied
      ? t("website.usage.buyCreditsPermissionTooltip")
      : isTrial
        ? t("website.usage.buyCreditsTrialTooltip")
        : buyButtonDisabled
          ? t("website.usage.upgradeToPurchase")
          : undefined;
    const buyButtonTitle = buyButtonTooltip;
    const shouldDimTopUpCredits =
      extraCreditsState === "frozen" ||
      extraCreditsState === "expired" ||
      extraCreditsState === "blocked";

    switch (extraCreditsState) {
      case "available":
        footer = t("website.usage.usePurchasedCreditsDescription");
        break;
      case "frozen":
        footer = t("website.usage.frozenCreditsDescription");
        amountClassName = "text-gray-400 opacity-60";
        break;
      case "expired":
        statusLabel = t("website.usage.expired");
        statusBadgeClass = "bg-[#FEF3C7] text-[#D97706]";
        footer = t("website.usage.expiredCreditsDescription");
        amountClassName = "text-gray-400 opacity-60";
        break;
      case "blocked":
        statusLabel = t("website.usage.blocked");
        statusBadgeClass = "bg-[#FEF3C7] text-[#D97706]";
        footer = t("website.usage.purchaseBlockedDescription");
        break;
      case "empty":
      default:
        footer = t("website.usage.usePurchasedCreditsDescription");
        break;
    }

    return {
      id: "topup",
      label: t("website.usage.purchasedCredits"),
      amount: formatCreditsValue(normalizedRemaining, locale),
      amountSuffix: t("website.usage.amountRemainingSuffix"),
      secondaryAmount: "",
      footer,
      progressPct: remainingPct,
      progressColor: shouldDimTopUpCredits
        ? "#9CA3AF"
        : getCreditsProgressColor(remainingPct),
      tooltipText: "",
      showBuyButton,
      buyButtonDisabled,
      buyButtonTitle,
      buyButtonTooltip,
      buyButtonAction: "open_buy_modal",
      statusLabel,
      statusBadgeClass,
      amountClassName,
    };
  }, [
    creditSummary?.canBuyTopup,
    creditSummary?.can_buy_topup,
    creditSummary?.extraCreditsState,
    creditSummary?.extra_credits_state,
    creditSummary?.purchased,
    creditSummary?.reasonCode,
    creditSummary?.reason_code,
    locale,
    sortedCreditGrants,
    t,
    topupEligibility?.eligible,
    topupEligibility?.reasonCode,
    topupOptionsError,
    hasResolvedTopupEligibility,
    isTrial,
    usageStatus,
  ]);
  const shouldShowPurchasedWorkUnits = !shouldUsePluginWorkspaceUi;
  const creditSummaryCards = shouldShowPurchasedWorkUnits
    ? [planSummaryCard, topUpSummaryCard]
    : [planSummaryCard];
  const currentTopUpCreditBalance =
    (getPurchasedSummaryDisplayMicrousd(
      creditSummary?.purchased,
      [
        "workUnitsRemainingDisplayMicrousd",
        "work_units_remaining_display_microusd",
        "displayRemainingMicrousd",
        "display_remaining_microusd",
      ],
    ) ?? 0) / MICROUSD_PER_CREDIT;
  const parsedAutoReloadMinimumBalance = useMemo(
    () => parseAutoReloadBalance(autoReloadDraft.minimumBalance),
    [autoReloadDraft.minimumBalance],
  );
  const parsedAutoReloadTargetBalance = useMemo(
    () => parseAutoReloadBalance(autoReloadDraft.targetBalance),
    [autoReloadDraft.targetBalance],
  );
  const isAutoReloadDraftValid = Boolean(
    parsedAutoReloadMinimumBalance !== null &&
    parsedAutoReloadTargetBalance !== null &&
    parsedAutoReloadTargetBalance > parsedAutoReloadMinimumBalance,
  );
  const minimumAutoReloadPurchase = isAutoReloadDraftValid
    ? Math.max(
        0,
        parsedAutoReloadTargetBalance! - parsedAutoReloadMinimumBalance!,
      )
    : 0;
  const initialAutoReloadPurchase = isAutoReloadDraftValid
    ? Math.max(0, parsedAutoReloadTargetBalance! - currentTopUpCreditBalance)
    : 0;
  const isAutoReloadDraftDirty = Boolean(
    parsedAutoReloadMinimumBalance !== autoReloadSettings.minimumBalance ||
    parsedAutoReloadTargetBalance !== autoReloadSettings.targetBalance,
  );
  const isAutoReloadActionLoading = autoReloadPendingAction !== null;
  const openAutoReloadModal = () => {
    setAutoReloadDraft({
      minimumBalance: String(autoReloadSettings.minimumBalance),
      targetBalance: String(autoReloadSettings.targetBalance),
    });
    setIsAutoReloadModalOpen(true);
  };
  const closeAutoReloadModal = () => {
    setIsAutoReloadModalOpen(false);
    setAutoReloadDraft({
      minimumBalance: String(autoReloadSettings.minimumBalance),
      targetBalance: String(autoReloadSettings.targetBalance),
    });
  };
  const handleAutoReloadDraftChange =
    (field: "minimumBalance" | "targetBalance") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const normalizedValue = event.target.value.replace(/\D/g, "");
      setAutoReloadDraft((currentDraft) => ({
        ...currentDraft,
        [field]: normalizedValue,
      }));
    };
  const handleTurnOnAutoReload = async () => {
    if (
      parsedAutoReloadMinimumBalance === null ||
      parsedAutoReloadTargetBalance === null ||
      !isAutoReloadDraftValid
    ) {
      return;
    }

    setAutoReloadPendingAction("turn_on");
    try {
      await new Promise((resolve) =>
        window.setTimeout(resolve, AUTO_RELOAD_ACTION_DELAY_MS),
      );
      setAutoReloadSettings({
        isEnabled: true,
        minimumBalance: parsedAutoReloadMinimumBalance,
        targetBalance: parsedAutoReloadTargetBalance,
      });
      setIsAutoReloadModalOpen(false);
      showToast(t("website.usage.enabledAutoReload"), "success");
    } finally {
      setAutoReloadPendingAction(null);
    }
  };
  const handleSaveAutoReload = () => {
    if (
      parsedAutoReloadMinimumBalance === null ||
      parsedAutoReloadTargetBalance === null ||
      !isAutoReloadDraftValid ||
      !isAutoReloadDraftDirty
    ) {
      return;
    }

    setAutoReloadSettings((currentSettings) => ({
      ...currentSettings,
      minimumBalance: parsedAutoReloadMinimumBalance,
      targetBalance: parsedAutoReloadTargetBalance,
    }));
    setIsAutoReloadModalOpen(false);
    showToast(t("website.usage.savedAutoReloadSettings"), "success");
  };
  const handleTurnOffAutoReload = async () => {
    setAutoReloadPendingAction("turn_off");
    try {
      await new Promise((resolve) =>
        window.setTimeout(resolve, AUTO_RELOAD_ACTION_DELAY_MS),
      );
      setAutoReloadSettings((currentSettings) => ({
        ...currentSettings,
        isEnabled: false,
      }));
      setIsAutoReloadModalOpen(false);
      showToast(t("website.usage.disabledAutoReload"), "success");
    } finally {
      setAutoReloadPendingAction(null);
    }
  };
  const tableGridTemplate = showUserColumn
    ? OWNER_ADMIN_TABLE_GRID
    : MEMBER_TABLE_GRID;
  const taskColumnOffsetClass = showUserColumn ? "pl-2" : "pl-4";
  const usageRecordNameColumnKey = !supportsUsageCategories
    ? "website.usage.task"
    : usageCategory === "market_intelligence"
      ? "website.usage.monitorName"
      : "website.usage.taskName";
  const hasSummaryData = Boolean(usageSummary || creditSummary);
  const shouldShowSummaryLoading =
    showWorkspaceSummary &&
    ((isLoadingWindow && !hasSummaryData) || !hasResolvedTopupEligibility);
  const shouldShowInitialEventsLoading =
    (isWorkspaceContextLoading || canViewUsage) && !hasResolvedInitialEvents;
  const shouldShowEventsLoading =
    isLoadingEvents || shouldShowInitialEventsLoading;
  const handleCreditsCtaClick = (card: CreditsSummaryCard) => {
    if (card.buyButtonDisabled) return;
    setIsBuyMoreModalOpen(true);
  };

  if (isAccessEvaluating) {
    return (
      <div className="space-y-8">
        <div className="space-y-6">
          <div className="space-y-4">
            <Skeleton className="h-5 w-24 rounded-full" />
            <CreditsSummaryCardSkeleton showStatusBadge />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-5 w-16 rounded-full" />
            <CreditsSummaryCardSkeleton
              showActionButton
              showProgressBar={false}
            />
          </div>
        </div>

        <div className="space-y-4">
          <Skeleton className="h-5 w-28 rounded-full" />
          <UsageRecordSkeleton
            showUserColumn
            tableGridTemplate={OWNER_ADMIN_TABLE_GRID}
            taskColumnOffsetClass="pl-2"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {showWorkspaceSummary && (
          <div>
            {shouldShowSummaryLoading ? (
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">
                    {t("website.usage.yourPlan")}
                  </h3>
                  <CreditsSummaryCardSkeleton showStatusBadge />
                </div>
                <div className="space-y-4">
                  <h3 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">
                    {t("website.usage.credit")}
                  </h3>
                  <CreditsSummaryCardSkeleton
                    showActionButton
                    showProgressBar={false}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Your plan category */}
                <div className="space-y-4">
                  <h3 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">
                    {t("website.usage.yourPlan")}
                  </h3>
                  <div className="rounded-2xl border border-[#E8EBF3] bg-white p-5 shadow-[0_10px_24px_rgba(26,22,47,0.06)] flex flex-col justify-between min-h-[142px]">
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-medium text-[#1A162F]">
                          {planSummaryCard.label}
                        </span>
                        {planSummaryCard.statusLabel &&
                        planSummaryCard.statusBadgeClass ? (
                          <span
                            className={clsx(
                              "rounded-full px-2 py-0.5 text-[10px] font-medium",
                              planSummaryCard.statusBadgeClass,
                            )}
                          >
                            {planSummaryCard.statusLabel}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-baseline justify-end gap-1.5 text-right whitespace-nowrap">
                          <span className="text-[18px] font-medium [font-variant-numeric:tabular-nums] text-[#1A162F]">
                            {planSummaryCard.amount}
                          </span>
                          {planSummaryCard.amountSuffix ? (
                            <span className="text-[13px] font-normal text-[#1A162F]">
                              {planSummaryCard.amountSuffix}
                            </span>
                          ) : null}
                        </div>
                        {planSummaryCard.shopifyPendingPaymentTooltip ? (
                          <button
                            type="button"
                            aria-label={t(
                              "website.usage.shopifyPendingPaymentTooltipLabel",
                            )}
                            className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#C8D0E2] text-[10px] font-semibold leading-none text-[#666F8D] transition-colors hover:border-[#1A162F] hover:text-[#1A162F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A162F]"
                            onMouseEnter={(event) => {
                              const rect =
                                event.currentTarget.getBoundingClientRect();
                              setProgressTooltip({
                                left: rect.left + rect.width / 2,
                                top: rect.bottom,
                                align: "right",
                                placement: "bottom",
                                text: (
                                  <span className="block max-w-[360px] whitespace-normal leading-5">
                                    {
                                      planSummaryCard.shopifyPendingPaymentTooltip
                                    }
                                  </span>
                                ),
                              });
                            }}
                            onMouseLeave={() => setProgressTooltip(null)}
                            onFocus={(event) => {
                              const rect =
                                event.currentTarget.getBoundingClientRect();
                              setProgressTooltip({
                                left: rect.left + rect.width / 2,
                                top: rect.bottom,
                                align: "right",
                                placement: "bottom",
                                text: (
                                  <span className="block max-w-[360px] whitespace-normal leading-5">
                                    {
                                      planSummaryCard.shopifyPendingPaymentTooltip
                                    }
                                  </span>
                                ),
                              });
                            }}
                            onBlur={() => setProgressTooltip(null)}
                          >
                            ?
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div
                      className="relative mb-3 h-2 cursor-default overflow-hidden rounded-full bg-[#ECEFF5]"
                      onMouseEnter={(event) => {
                        if (!planSummaryCard.tooltipText) return;
                        const rect =
                          event.currentTarget.getBoundingClientRect();
                        setProgressTooltip({
                          left: rect.left + rect.width / 2,
                          top: rect.top,
                          text: planSummaryCard.tooltipText,
                        });
                      }}
                      onMouseLeave={() => setProgressTooltip(null)}
                    >
                      <div
                        className="absolute top-0 left-0 h-full rounded-full transition-all duration-1000 ease-out"
                        style={{
                          width: `${planSummaryCard.progressPct}%`,
                          backgroundColor: planSummaryCard.progressColor,
                        }}
                      />
                    </div>

                    <div className="text-xs font-normal leading-5 text-[#666F8D]">
                      {planSummaryCard.footer}
                    </div>
                  </div>
                </div>

                {shouldShowPurchasedWorkUnits ? (
                  <div className="space-y-4">
                    <h3 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">
                      {t("website.usage.credit")}
                    </h3>
                    <div className="rounded-2xl border border-[#E8EBF3] bg-white p-5 shadow-[0_10px_24px_rgba(26,22,47,0.06)] flex flex-col gap-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="flex items-baseline gap-1.5">
                          <span
                          className={clsx(
                            "text-[20px] font-semibold [font-variant-numeric:tabular-nums]",
                            topUpSummaryCard.amountClassName || "text-[#1A162F]",
                          )}
                          >
                            {topUpSummaryCard.amount}
                          </span>
                          {topUpSummaryCard.amountSuffix ? (
                            <span className="text-[13px] font-normal text-[#1A162F]">
                              {topUpSummaryCard.amountSuffix}
                            </span>
                          ) : null}
                        </div>
                        {topUpSummaryCard.showBuyButton ? (
                          <div
                            className={clsx(
                              "shrink-0",
                              topUpSummaryCard.buyButtonDisabled
                                ? "cursor-not-allowed"
                                : "",
                            )}
                            title={topUpSummaryCard.buyButtonTitle}
                            onMouseEnter={(event) => {
                              if (
                                !topUpSummaryCard.buyButtonDisabled ||
                                !topUpSummaryCard.buyButtonTooltip
                              ) {
                                return;
                              }
                              const rect =
                                event.currentTarget.getBoundingClientRect();
                              setBuyButtonTooltip({
                                left: rect.left + rect.width / 2,
                                top: rect.top,
                                text: topUpSummaryCard.buyButtonTooltip,
                              });
                            }}
                            onMouseLeave={() => setBuyButtonTooltip(null)}
                          >
                            <button
                              onClick={() =>
                                handleCreditsCtaClick(topUpSummaryCard)
                              }
                              disabled={Boolean(
                                topUpSummaryCard.buyButtonDisabled,
                              )}
                              title={topUpSummaryCard.buyButtonTitle}
                              className={clsx(
                                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white",
                                topUpSummaryCard.buyButtonDisabled
                                  ? "cursor-not-allowed bg-[#D1D7E6]"
                                  : "bg-[#1A162F] transition-colors hover:bg-[#231D3E]",
                              )}
                            >
                              <Plus size={12} weight="bold" />
                            </button>
                          </div>
                        ) : null}
                        {topUpSummaryCard.statusLabel &&
                        topUpSummaryCard.statusBadgeClass ? (
                          <span
                            className={clsx(
                              "rounded-full px-2 py-0.5 text-[10px] font-medium",
                              topUpSummaryCard.statusBadgeClass,
                            )}
                          >
                            {topUpSummaryCard.statusLabel}
                          </span>
                        ) : null}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-normal leading-5 text-[#666F8D]">
                        <span>{topUpSummaryCard.footer}</span>
                        {sortedCreditGrants.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setIsGrantsModalOpen(true)}
                            className="font-medium text-[#1A162F] transition-colors hover:text-[#231D3E] hover:underline whitespace-nowrap"
                          >
                            {t("website.usage.viewDetails")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}

        {SHOW_AUTO_RELOAD ? (
          shouldShowPurchasedWorkUnits ? (
          <div className="space-y-4">
            <h3 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">
              {t("website.usage.autoReload")}
            </h3>
            <div className="rounded-[20px] border border-[#E8EBF3] bg-white px-6 py-5 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <h4 className="text-[16px] font-medium tracking-[-0.01em] text-[#1A162F]">
                      {t("website.usage.autoReloadCredit")}
                    </h4>
                    {autoReloadSettings.isEnabled ? (
                      <span className="inline-flex items-center gap-1 text-[13px] font-medium text-[#22C55E]">
                        <CheckCircle
                          size={13}
                          weight="fill"
                          className="shrink-0"
                        />
                        {t("website.usage.active")}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-[13px] font-normal leading-6 text-[#666F8D]">
                    {t("website.usage.autoReloadDescription")}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={openAutoReloadModal}
                  className="inline-flex h-9 shrink-0 items-center justify-center rounded-full border border-[#D7DEEC] bg-white px-4 text-[13px] font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC]"
                >
                  {t("common.settings")}
                </button>
              </div>
            </div>
          </div>
          ) : null
        ) : null}

        {canViewUsage ? (
          <div className="space-y-4">
            <h3 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">
              {t("website.usage.usageRecord")}
            </h3>
            {supportsUsageCategories && (
              <div className="flex gap-6 border-b border-[#E8EBF3]" role="tablist" aria-label={t("website.usage.usageRecord")}>
                {(["client", "market_intelligence"] as const).map(category => (
                  <button
                    type="button"
                    role="tab"
                    key={category}
                    id={`usage-tab-${category}`}
                    aria-controls="usage-record-panel"
                    aria-selected={usageCategory === category}
                    onClick={() => {
                      if (usageCategory === category) return;
                      setUsageCategory(category);
                      setCurrentPage(1);
                      setEvents([]);
                      setTotalEvents(0);
                      setUsageEventsError(false);
                      setIsLoadingEvents(true);
                      setTaskTooltip(null);
                    }}
                    className={clsx("border-b-2 px-1 py-3 text-sm font-medium", usageCategory === category
                      ? "border-[#1A162F] text-[#1A162F]" : "border-transparent text-[#666F8D]")}
                  >
                    {t(category === "client" ? "website.usage.clientUsage" : "website.usage.marketUsage")}
                  </button>
                ))}
              </div>
            )}
            {!(supportsUsageCategories && usageCategory === "market_intelligence") && usageEventsError && (
              <div role="alert" className="flex items-center gap-3 text-sm text-[#666F8D]">
                {t("website.usage.recordsLoadError")}
                <button type="button" onClick={() => setUsageEventsRetry(value => value + 1)} className="underline">
                  {t("website.usage.retryRecords")}
                </button>
              </div>
            )}
            {supportsUsageCategories && usageCategory === "market_intelligence" ? (
              <MarketUsageTree key={`${workspaceId}:${topupReturnRefreshKey}`} />
            ) : <div id="usage-record-panel" role={supportsUsageCategories ? "tabpanel" : undefined}
              aria-labelledby={supportsUsageCategories ? `usage-tab-${usageCategory}` : undefined}
              className="overflow-hidden rounded-[20px] border border-[#E8EBF3] bg-white shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
              <div className="overflow-x-auto">
                <div className={clsx(showUserColumn ? "min-w-[840px]" : "min-w-[680px]")}>
              <div className="border-b border-[#ECEFF5] bg-[#FCFCFC] px-6 py-3">
                <div
                  className="grid items-center gap-0"
                  style={{ gridTemplateColumns: tableGridTemplate }}
                >
                  <div
                    className={clsx(
                      taskColumnOffsetClass,
                      "min-w-0 pr-3 text-left text-xs font-medium text-[#8A93B2]",
                    )}
                  >
                    {t(usageRecordNameColumnKey)}
                  </div>
                  <div className="pl-5 text-left text-xs font-medium text-[#8A93B2]">
                    {t("website.usage.time")}
                  </div>
                  {showUserColumn && (
                    <div className="min-w-0 pr-4 text-left text-xs font-medium text-[#8A93B2]">
                      {t("website.usage.user")}
                    </div>
                  )}
                  <div className="justify-self-start text-left text-xs font-medium text-[#8A93B2] [font-variant-numeric:tabular-nums]">
                    {t("website.usage.creditsChange")}
                  </div>
                </div>
              </div>

              <div className="divide-y divide-[#ECEFF5]">
                {shouldShowEventsLoading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <div key={idx} className="px-6 py-4">
                      <div
                        className="grid items-center gap-0"
                        style={{ gridTemplateColumns: tableGridTemplate }}
                      >
                        <div className="min-w-0">
                          <Skeleton className="h-5 w-40" />
                        </div>
                        <div>
                          <Skeleton className="h-5 w-24" />
                        </div>
                        {showUserColumn && (
                          <div className="min-w-0">
                            <Skeleton className="h-5 w-40" />
                          </div>
                        )}
                        <div>
                          <Skeleton className="h-5 w-16" />
                        </div>
                      </div>
                    </div>
                  ))
                ) : events.length > 0 ? (
                  events.map((event) => {
                    const taskName = getTaskName(
                      event.session_name,
                      t("website.usage.agentSelfImproving"),
                    );
                    const usageDetail = event.usage_category === "market_intelligence"
                      ? event.usage_stage === "analysis"
                        ? t("website.usage.monitorAnalysis")
                        : event.usage_stage === "collection"
                          ? `${getUsageChannelLabel(event.usage_channel) || t("website.usage.channelNotRecorded")} · ${t("website.usage.monitorCollection")}`
                          : null
                      : null;
                    const userEmail = getUserEmail(event.session_user_email);
                    const userName = getUserName(
                      event.session_user_name,
                      event.session_user_email,
                    );
                    const eventTime = formatUsageEventTime(
                      event.created_at,
                      locale,
                    );
                    return (
                      <div
                        key={event.id}
                        className="px-6 py-4 transition-colors hover:bg-[#F8F9FC]"
                      >
                        <div
                          className="grid items-center gap-0"
                          style={{ gridTemplateColumns: tableGridTemplate }}
                        >
                          <div
                            className={clsx(
                              taskColumnOffsetClass,
                              "min-w-0 overflow-hidden pr-3 text-left text-sm font-normal text-[#1A162F]",
                            )}
                          >
                            <span
                              className="block max-w-full truncate cursor-help"
                              title={taskName}
                              onMouseEnter={(e) => showTaskTooltip(e, taskName)}
                              onMouseLeave={() => setTaskTooltip(null)}
                              onFocus={(e) => showTaskTooltip(e, taskName)}
                              onBlur={() => setTaskTooltip(null)}
                              tabIndex={0}
                            >
                              {taskName}
                            </span>
                            {usageDetail && (
                              <span className="mt-1 block text-xs leading-4 text-[#666F8D]">
                                {usageDetail}
                              </span>
                            )}
                          </div>
                          <div className="pl-5 text-left min-w-0">
                            <div className="whitespace-nowrap text-sm font-normal leading-5 text-[#1A162F]">
                              {eventTime.localTime}
                            </div>
                            {eventTime.timezone && (
                              <div className="mt-0.5 text-xs font-normal leading-4 text-[#666F8D]">
                                {eventTime.timezone}
                              </div>
                            )}
                          </div>
                          {showUserColumn && (
                            <div className="min-w-0 pr-4 text-left leading-tight">
                              <div
                                className="block max-w-full truncate text-sm font-medium text-[#1A162F]"
                                title={userName}
                              >
                                {userName}
                              </div>
                              {userEmail !== "--" && (
                                <div
                                  className="mt-1 block max-w-full truncate text-xs font-normal leading-4 text-[#666F8D]"
                                  title={userEmail}
                                >
                                  {userEmail}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="justify-self-start whitespace-nowrap text-left text-sm font-medium text-[#1A162F] [font-variant-numeric:tabular-nums]">
                            {formatCreditsChange(
                              event.display_cost_microusd,
                              locale,
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-16 text-center">
                    <p className="text-sm font-normal text-[#666F8D]">
                      {usageEventsError ? "" : t(usageCategory === "market_intelligence" && supportsUsageCategories
                        ? "website.usage.noMarketUsage" : "website.usage.noUsageEvents")}
                    </p>
                  </div>
                )}
              </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-[#ECEFF5] bg-[#FCFCFC] px-6 py-4">
                <span className="text-sm font-normal text-[#666F8D]">
                  {totalEvents > 0
                    ? t("website.usage.showingEventsRange", {
                        start: formatIntegerForDisplay(
                          (currentPage - 1) * ITEMS_PER_PAGE + 1,
                          locale,
                        ),
                        end: formatIntegerForDisplay(
                          Math.min(currentPage * ITEMS_PER_PAGE, totalEvents),
                          locale,
                        ),
                        total: formatIntegerForDisplay(totalEvents, locale),
                      })
                    : t("website.usage.showingZeroEvents", {
                        total: formatIntegerForDisplay(0, locale),
                      })}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setCurrentPage((page) => Math.max(1, page - 1))
                    }
                    disabled={currentPage === 1}
                    className="rounded-lg p-2 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <CaretLeft size={16} weight="regular" />
                  </button>
                  <div className="flex items-center gap-1">
                    {visiblePages.map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={clsx(
                          "h-8 w-8 rounded-lg text-sm font-medium transition-colors",
                          currentPage === page
                            ? "bg-[#1A162F] text-white hover:bg-primary/90"
                            : "text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]",
                        )}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((page) => Math.min(totalPages, page + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="rounded-lg p-2 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <CaretRight size={16} weight="regular" />
                  </button>
                </div>
              </div>
            </div>}
          </div>
        ) : (
          <div className="space-y-4">
            <h3 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">
              {t("website.usage.usageRecord")}
            </h3>
            <div className="overflow-hidden rounded-[20px] border border-[#E8EBF3] bg-white shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
              <div className="flex min-h-[220px] flex-col items-center justify-center px-6 py-12 text-center text-gray-500">
                <p className="text-lg font-medium">
                  {t("website.usage.permissionDenied")}
                </p>
                <p className="mt-2 text-sm">
                  {t("website.usage.noPermissionViewUsageDetails")}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {taskTooltip &&
        createPortal(
          <div
            className="fixed z-[30000] rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg pointer-events-none max-w-[420px] break-words"
            style={{
              left: taskTooltip.left,
              top: taskTooltip.top,
              transform: "translate(-50%, calc(-100% - 10px))",
            }}
          >
            {taskTooltip.text}
            <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          </div>,
          document.body,
        )}
      {progressTooltip &&
        createPortal(
          <div
            className={clsx(
              "fixed z-[30000] rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg pointer-events-none",
              typeof progressTooltip.text === "string"
                ? "whitespace-nowrap"
                : "w-[360px] max-w-[calc(100vw-32px)]",
            )}
            style={{
              left: progressTooltip.left,
              top: progressTooltip.top,
              transform:
                progressTooltip.placement === "bottom" &&
                progressTooltip.align === "right"
                  ? "translate(calc(-100% + 14px), 10px)"
                  : progressTooltip.placement === "bottom"
                    ? "translate(-50%, 10px)"
                    : progressTooltip.align === "right"
                      ? "translate(calc(-100% + 14px), calc(-100% - 10px))"
                      : "translate(-50%, calc(-100% - 10px))",
            }}
          >
            {progressTooltip.text}
            <div
              className={clsx(
                "absolute border-4 border-transparent",
                progressTooltip.placement === "bottom"
                  ? "bottom-full border-b-gray-900"
                  : "top-full border-t-gray-900",
                progressTooltip.align === "right"
                  ? "right-[10px]"
                  : "left-1/2 -translate-x-1/2",
              )}
            />
          </div>,
          document.body,
        )}
      {buyButtonTooltip &&
        createPortal(
          <div
            className="fixed z-[30000] rounded-lg bg-gray-900 px-3 py-1.5 text-xs whitespace-nowrap text-white shadow-lg pointer-events-none"
            style={{
              left: buyButtonTooltip.left,
              top: buyButtonTooltip.top,
              transform: "translate(-50%, calc(-100% - 10px))",
            }}
          >
            {buyButtonTooltip.text}
            <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          </div>,
          document.body,
        )}
      {shouldShowPurchasedWorkUnits && isBuyMoreModalOpen ? (
        <BuyMoreCreditsModal
          isOpen={isBuyMoreModalOpen}
          onClose={() => setIsBuyMoreModalOpen(false)}
          workspaceId={workspaceId}
          skuOptions={topupSkus}
          eligibility={topupEligibility}
          isTopupDataLoading={isLoadingTopupOptions}
          topupDataError={topupOptionsError}
          onRetryTopupData={loadCreditTopupOptions}
        />
      ) : null}
      {shouldShowPurchasedWorkUnits && isGrantsModalOpen ? (
        <Modal
        isOpen={isGrantsModalOpen}
        onClose={() => setIsGrantsModalOpen(false)}
        title={t("website.usage.purchasedCreditPackages")}
        width="max-w-[600px]"
        bodyClassName="px-6 pb-6 pt-2"
      >
        <div className="mb-5 text-sm text-[#666F8D] leading-6">
          {t("website.usage.purchasedCreditPackagesDescription")}
        </div>

        <div className="overflow-hidden rounded-xl border border-[#E8EBF3] bg-white">
          <table className="w-full text-left text-sm text-[#1A162F] table-fixed">
            <thead className="bg-[#F8F9FC] text-xs font-medium text-[#8A93B2] border-b border-[#E8EBF3]">
              <tr>
                <th className="px-4 py-3 w-[25%] font-medium">
                  {t("website.usage.received")}
                </th>
                <th className="px-4 py-3 w-[18%] font-medium">
                  {t("website.usage.status")}
                </th>
                <th className="px-4 py-3 text-left w-[32%] font-medium">
                  {t("website.usage.balance")}
                </th>
                <th className="px-4 py-3 w-[25%] font-medium">
                  {t("website.usage.expiresAt")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8EBF3]">
              {paginatedGrants.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-8 text-center text-sm text-[#666F8D]"
                  >
                    {t("website.usage.noPurchasedCreditPackages")}
                  </td>
                </tr>
              ) : null}
              {paginatedGrants.map((grant) => {
                const displayStatus = getCreditGrantDisplayStatus(grant);
                return (
                  <tr
                    key={grant.id}
                    className="hover:bg-[#F8F9FC]/50 transition-colors"
                  >
                    <td className="px-4 py-3.5 text-xs text-[#666F8D]">
                      {formatDateOnly(grant.purchasedAt, locale)}
                    </td>
                    <td className="px-4 py-3.5 text-xs">
                      <span
                        className={clsx(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
                          getCreditGrantStatusBadgeClass(displayStatus),
                        )}
                      >
                        {getCreditGrantStatusLabel(displayStatus, t)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-left text-xs font-medium">
                      {formatCreditsValue(
                        grant.remainingCreditsMicrousd,
                        locale,
                      )}
                      <span className="text-[#8A93B2] font-normal">
                        {" / "}
                        {formatCreditsValue(
                          grant.purchasedCreditsMicrousd,
                          locale,
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-[#666F8D]">
                      {formatDateOnly(grant.expiresAt, locale)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sortedCreditGrants.length > grantsPerPage && (
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-[#666F8D]">
              {t("website.usage.showingRangeOfTotal", {
                start: formatIntegerForDisplay(
                  Math.min(
                    sortedCreditGrants.length,
                    (grantsPage - 1) * grantsPerPage + 1,
                  ),
                  locale,
                ),
                end: formatIntegerForDisplay(
                  Math.min(
                    sortedCreditGrants.length,
                    grantsPage * grantsPerPage,
                  ),
                  locale,
                ),
                total: formatIntegerForDisplay(
                  sortedCreditGrants.length,
                  locale,
                ),
              })}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setGrantsPage((page) => Math.max(1, page - 1))}
                disabled={grantsPage === 1}
                className="rounded-lg p-1.5 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CaretLeft size={16} weight="regular" />
              </button>
              <div className="flex items-center gap-0.5">
                {visibleGrantPages.map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setGrantsPage(page)}
                    className={clsx(
                      "h-7 w-7 rounded-lg text-xs font-semibold transition-colors",
                      grantsPage === page
                        ? "bg-[#1A162F] text-white hover:bg-[#1A162F]/90"
                        : "text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]",
                    )}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  setGrantsPage((page) => Math.min(totalGrantsPages, page + 1))
                }
                disabled={grantsPage === totalGrantsPages}
                className="rounded-lg p-1.5 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CaretRight size={16} weight="regular" />
              </button>
            </div>
          </div>
        )}
        </Modal>
      ) : null}
      {SHOW_AUTO_RELOAD ? (
        shouldShowPurchasedWorkUnits ? (
        <Modal
          isOpen={isAutoReloadModalOpen}
          onClose={
            isAutoReloadActionLoading ? () => undefined : closeAutoReloadModal
          }
          title={t("website.usage.autoReloadCredit")}
          width="max-w-[560px]"
          titleClassName="text-[16px] font-medium tracking-[-0.01em] text-[#1A162F]"
          closeButtonClassName={
            isAutoReloadActionLoading
              ? "pointer-events-none opacity-50"
              : undefined
          }
        >
          <div className="space-y-5">
            <div className="space-y-2">
              <h4 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">
                {t("website.usage.minimumBalance")}
              </h4>
              <p className="text-[13px] leading-6 text-[#666F8D]">
                {t("website.usage.minimumBalanceDescription")}
              </p>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={autoReloadDraft.minimumBalance}
                onChange={handleAutoReloadDraftChange("minimumBalance")}
                disabled={isAutoReloadActionLoading}
                className="h-12 w-full rounded-[14px] border border-[#D7DEEC] bg-white px-4 text-[16px] font-medium text-[#1A162F] outline-none transition-colors placeholder:text-[#C8D0E2] focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/10"
              />
            </div>

            <div className="space-y-2">
              <h4 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">
                {t("website.usage.targetBalance")}
              </h4>
              <p className="text-[13px] leading-6 text-[#666F8D]">
                {t("website.usage.targetBalanceDescription")}
              </p>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={autoReloadDraft.targetBalance}
                onChange={handleAutoReloadDraftChange("targetBalance")}
                disabled={isAutoReloadActionLoading}
                className="h-12 w-full rounded-[14px] border border-[#D7DEEC] bg-white px-4 text-[16px] font-medium text-[#1A162F] outline-none transition-colors placeholder:text-[#C8D0E2] focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/10"
              />
            </div>

            <p className="text-[13px] leading-6 text-[#666F8D]">
              {t("website.usage.minimumPurchase", {
                credits: formatIntegerForDisplay(
                  minimumAutoReloadPurchase,
                  locale,
                ),
              })}{" "}
              <span className="font-semibold text-[#1A162F]">
                {formatAutoReloadCost(minimumAutoReloadPurchase, locale)}
              </span>
            </p>

            <p className="text-[13px] leading-6 text-[#666F8D]">
              {t("website.usage.autoChargeDescription")}
            </p>

            {!autoReloadSettings.isEnabled ? (
              <div className="rounded-[20px] border border-[#ECEFF5] bg-[#FCFCFC] px-5 py-4 text-[13px] leading-6 text-[#1A162F]">
                {t("website.usage.autoReloadInitialPurchase", {
                  credits: formatIntegerForDisplay(
                    initialAutoReloadPurchase,
                    locale,
                  ),
                })}{" "}
                <span className="font-semibold">
                  {formatAutoReloadCost(initialAutoReloadPurchase, locale)}
                </span>
              </div>
            ) : null}

            <div className="flex items-center justify-end gap-3 pt-2">
              {autoReloadSettings.isEnabled ? (
                <>
                  <button
                    type="button"
                    onClick={handleTurnOffAutoReload}
                    disabled={isAutoReloadActionLoading}
                    className={clsx(
                      "inline-flex min-w-[116px] items-center justify-center gap-2 rounded-full border border-[#D7DEEC] bg-white px-5 py-2.5 text-sm font-medium text-[#1A162F] transition-colors",
                      isAutoReloadActionLoading
                        ? "cursor-not-allowed opacity-70"
                        : "hover:bg-[#F8F9FC]",
                    )}
                  >
                    {autoReloadPendingAction === "turn_off" ? (
                      <>
                        <CircleNotch
                          size={16}
                          weight="regular"
                          className="animate-spin"
                        />
                        <span>{t("website.usage.turnOff")}</span>
                      </>
                    ) : (
                      t("website.usage.turnOff")
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAutoReload}
                    disabled={
                      isAutoReloadActionLoading ||
                      !isAutoReloadDraftValid ||
                      !isAutoReloadDraftDirty
                    }
                    className={clsx(
                      "rounded-full px-5 py-2.5 text-sm font-medium transition-colors",
                      !isAutoReloadActionLoading &&
                        isAutoReloadDraftValid &&
                        isAutoReloadDraftDirty
                        ? "bg-[#1A162F] text-white hover:bg-[#231D3E]"
                        : "cursor-not-allowed bg-[#D1D5DB] text-white",
                    )}
                  >
                    {t("common.save")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={closeAutoReloadModal}
                    disabled={isAutoReloadActionLoading}
                    className={clsx(
                      "rounded-full border border-[#D7DEEC] bg-white px-5 py-2.5 text-sm font-medium text-[#1A162F] transition-colors",
                      isAutoReloadActionLoading
                        ? "cursor-not-allowed opacity-70"
                        : "hover:bg-[#F8F9FC]",
                    )}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleTurnOnAutoReload}
                    disabled={
                      isAutoReloadActionLoading || !isAutoReloadDraftValid
                    }
                    className={clsx(
                      "inline-flex min-w-[116px] items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors",
                      !isAutoReloadActionLoading && isAutoReloadDraftValid
                        ? "bg-[#1A162F] text-white hover:bg-[#231D3E]"
                        : "cursor-not-allowed bg-[#D1D5DB] text-white",
                    )}
                  >
                    {autoReloadPendingAction === "turn_on" ? (
                      <>
                        <CircleNotch
                          size={16}
                          weight="regular"
                          className="animate-spin"
                        />
                        <span>{t("website.usage.turnOn")}</span>
                      </>
                    ) : (
                      t("website.usage.turnOn")
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </Modal>
        ) : null
      ) : null}
    </>
  );
};

const UsageSettings: React.FC = () => (
  isPluginProduct(getCurrentProductCode()) ? <PluginUsageSettings /> : <CoreUsageSettings />
);

export default UsageSettings;
